import { useCallback } from 'react';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { GridLayoutInfo } from '../types/customDashboard';
import { logger } from '../utils/logger';

interface FavoriteToggleParams {
  componentId: string;
  componentTitle: string;
  dashboardSource: string;
  componentType: 'chart' | 'kpi' | 'table' | 'mixed';
  description?: string;
  props?: Record<string, any>;
  gridLayout?: GridLayoutInfo;
  onToggleComplete?: (isFavorited: boolean) => void;
}

export const useFavoriteToggle = () => {
  const { addToFavorites, removeFromFavorites, isFavorited, favorites } = useFavorites();
  const { user } = useAuth();

  const handleFavoriteToggle = useCallback(async (params: FavoriteToggleParams) => {
    const {
      componentId,
      componentTitle,
      dashboardSource,
      componentType,
      description,
      props = {},
      gridLayout,
      onToggleComplete
    } = params;

    try {
      if (isFavorited(componentId)) {
        // Find and remove the favorite
        const favorite = favorites.find(f => f.componentId === componentId);
        if (favorite) {
          await removeFromFavorites(favorite.id);
          onToggleComplete?.(false);
        }
      } else {
        // Add to favorites
        await addToFavorites({
          componentId,
          dashboardSource,
          componentType,
          title: componentTitle,
          description,
          props,
          gridLayout
        });
        onToggleComplete?.(true);
      }
    } catch (error) {
      logger.error('Error toggling favorite', 'FavoriteToggle', error);
    }
  }, [addToFavorites, removeFromFavorites, isFavorited, favorites]);

  return { handleFavoriteToggle, isFavorited };
};